import { Coords } from 'google-map-react'
import haversine from 'haversine'
import _ from 'lodash'

import { calloutHeight, calloutMapMargin, calloutMarkerMargin, calloutWidth } from './constants'
import { markerHeightForPin, markerPositionOffsetForPin, markerWidthForPin } from './Marker/util'
import { MapState, Pin } from './types'

function coordsPosition(mapState: MapState, coords: Coords) {
  const { bounds, size } = mapState

  // Horizontal/vertical distances from the coords to each bound of the map area.
  const leftBoundDistance = haversine(
    { latitude: coords.lat, longitude: bounds.nw.lng },
    { latitude: coords.lat, longitude: coords.lng },
  )
  const rightBoundDistance = haversine(
    { latitude: coords.lat, longitude: coords.lng },
    { latitude: coords.lat, longitude: bounds.ne.lng },
  )
  const topBoundDistance = haversine(
    { latitude: bounds.nw.lat, longitude: coords.lng },
    { latitude: coords.lat, longitude: coords.lng },
  )
  const bottomBoundDistance = haversine(
    { latitude: coords.lat, longitude: coords.lng },
    { latitude: bounds.sw.lat, longitude: coords.lng },
  )

  const widthRatio = leftBoundDistance / (leftBoundDistance + rightBoundDistance)
  const heightRatio = topBoundDistance / (topBoundDistance + bottomBoundDistance)

  return {
    x: size.width * widthRatio,
    y: size.height * heightRatio,
  }
}

export function relativeCalloutOffset(mapState: MapState, markerCoords: Coords, markerPin: Pin) {
  const {
    size: { width },
  } = mapState

  // Position (in pixels) of the marker within the map area.
  const markerPosition = coordsPosition(mapState, markerCoords)

  // Natural (default) offset from the marker, i.e. given an infinitely large map area, the callout
  // would be positioned using this offset from the marker (above and partly towards the left of the
  // marker).
  const naturalOffset = {
    bottom: markerHeightForPin(markerPin) + calloutMarkerMargin,
    left: (-calloutWidth * 2) / 3 + markerWidthForPin(markerPin) / 2,
  }

  // Bounds of the callout as if it were positioned according to the natural offset.
  const naturalLeftBound = markerPosition.x + naturalOffset.left - markerWidthForPin(markerPin) / 2
  const naturalRightBound =
    markerPosition.x + naturalOffset.left + calloutWidth - markerWidthForPin(markerPin) / 2
  const naturalTopBound =
    markerPosition.y - calloutMarkerMargin + markerPositionOffsetForPin(markerPin) - calloutHeight

  let bottom = naturalOffset.bottom
  if (naturalTopBound < calloutMapMargin) {
    // Adjust natural bottom offset to ensure top edge of callout is within map area (taking into
    // account map margin). If the top edge would be out of bounds when naturally positioned,
    // position the callout below the marker instead.
    bottom = -calloutHeight - calloutMarkerMargin
  }

  let left = naturalOffset.left
  if (naturalLeftBound < calloutMapMargin) {
    // Adjust natural left offset to ensure left edge of callout is within map area (taking into
    // account map margin).
    left = naturalOffset.left - naturalLeftBound + calloutMapMargin
  } else if (width - naturalRightBound < calloutMapMargin) {
    // Adjust natural left offset to ensure right edge of callout is within map area (taking into
    // account map margin).
    left = naturalOffset.left - calloutMapMargin + width - naturalRightBound
  }

  return { bottom, left }
}
